/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabaseClient.js";
import { takeAuthRedirect } from "../utils/authRedirectHash.js";
import BrandLogo from "./BrandLogo.jsx";

// Step 2 of password reset: the recovery link drops the user here inside a
// temporary recovery session (detectSessionInUrl handles the token). They set
// a new password, which we save via updateUser, then send them to the app.
export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [ready, setReady]       = useState(false);
  const [status, setStatus]     = useState(null);
  const [busy, setBusy]         = useState(false);
  // Why the link failed, if it did. Claimed once at mount — captured at boot by
  // captureAuthRedirect() because the fragment doesn't survive a redirect.
  const [linkError] = useState(() => takeAuthRedirect()?.message ?? null);
  const navigate = useNavigate();

  useEffect(() => {
    // Confirm we actually have a recovery session before showing the form.
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setReady(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) {
      setStatus({ ok: false, msg: "Password must be at least 8 characters." });
      return;
    }
    if (password !== confirm) {
      setStatus({ ok: false, msg: "Passwords don't match." });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // Land on the operations hub, not inside the Distribution Map module.
      // This pointed at /dashboard from when that WAS the app's home screen —
      // so resetting a password dropped you into one product module for no
      // reason, and would have done so even for a tenant without the map.
      setStatus({ ok: true, msg: "Password updated. Taking you to your workspace…" });
      setTimeout(() => navigate("/", { replace: true }), 1200);
    } catch (err) {
      setStatus({ ok: false, msg: err.message || "Could not update password." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <section className="auth-card">
        <div className="auth-logo">
          <BrandLogo />
        </div>
        <h1>Set a new password</h1>
        <p className="auth-sub">Choose a new password for your account.</p>

        {!ready ? (
          // Say WHY when we know. The old copy ("if you got here another way…")
          // implied user error even when the real cause was an expired or
          // already-used link, which is the common case.
          linkError ? (
            <>
              <p className="error" style={{ textAlign: "center" }}>{linkError}</p>
              <p style={{ textAlign: "center" }}>
                <Link className="btn" to="/forgot-password">Send a new reset link</Link>
              </p>
            </>
          ) : (
            <p className="muted" style={{ textAlign: "center" }}>
              This page opens from the reset link in your email. If you got here another
              way, <Link to="/forgot-password">request a new link</Link>.
            </p>
          )
        ) : (
          <form onSubmit={handleSubmit}>
            <label>
              New password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </label>
            <label>
              Confirm password
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </label>
            <button className="btn" type="submit" disabled={busy}>
              {busy ? "Saving..." : "Update password"}
            </button>
          </form>
        )}

        {status && <p className={status.ok ? "success" : "error"}>{status.msg}</p>}
      </section>
    </div>
  );
}
